// Story 1.36 D1 — wallet-details form for /account/wallet.
//
// The identity precondition for ALL money movement: top-up and the PSD2
// refund each answer 412 `wallet_details_required` until these five fields
// are stored (routes/wallet.rs::ensure_user_wallet). This panel is where the
// SPA sends the user to clear that gate. (Story 8.3: cash-out and bank-account
// registration are no longer among the gated flows — there is no user payout.)
//
// Two states, mirroring OnboardingPanel's form/verified split:
//   * incomplete (or "Edit details") — the five-field form, pre-filled
//     from GET. Client-side niceties only (trim, uppercase 2-letter
//     country codes, non-empty checks); the SERVER stays the validator
//     (underage DOB, unknown country code) and its per-field message is
//     rendered under the offending input.
//   * complete — a compact summary + an "Edit details" affordance.
//
// The first name input carries a stable id (`wallet-details-first-name`)
// so the money flows can focus it when their 412 routes the user here.

import { useEffect, useState } from "react";
import { Button, Input, Label } from "@greenhold/components";
import toast from "react-hot-toast";

import {
  useWalletDetails,
  useSaveWalletDetails,
  type WalletDetailsError,
} from "./useWalletDetails";

/** Keep only letters, cap at two, uppercase — the shape the server's
 *  ISO 3166-1 alpha-2 check accepts. */
function normalizeCountry(raw: string): string {
  return raw.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase();
}

export function WalletDetailsPanel() {
  const details = useWalletDetails();
  const save = useSaveWalletDetails();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [nationality, setNationality] = useState("");
  const [residence, setResidence] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{
    field: string;
    message: string;
  } | null>(null);

  // Pre-fill once GET resolves (nulls → empty). Guarded by `initialized`
  // so the user's in-progress edits are never clobbered by a background
  // refetch.
  useEffect(() => {
    if (details.data && !initialized) {
      setFirstName(details.data.first_name ?? "");
      setLastName(details.data.last_name ?? "");
      setDob(details.data.date_of_birth ?? "");
      setNationality(details.data.nationality ?? "");
      setResidence(details.data.residence_country ?? "");
      setInitialized(true);
    }
  }, [details.data, initialized]);

  if (details.isPending) {
    return (
      <p className="text-sm text-muted-foreground">Loading your details…</p>
    );
  }

  const complete = details.data?.complete ?? false;

  if (complete && !editing) {
    const d = details.data!;
    return (
      <div className="space-y-2" data-testid="wallet-details-complete">
        <p className="text-sm font-medium">Wallet details complete</p>
        <p className="text-sm text-muted-foreground">
          {d.first_name} {d.last_name} · {d.nationality} · born{" "}
          {d.date_of_birth}
        </p>
        <Button
          type="button"
          variant="tertiary"
          onClick={() => {
            setInitialized(false); // re-sync the form from the stored state
            setEditing(true);
          }}
        >
          Edit details
        </Button>
      </div>
    );
  }

  const errFor = (field: string) =>
    fieldError?.field === field ? fieldError.message : null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setFieldError(null);

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const nat = normalizeCountry(nationality);
    const res = normalizeCountry(residence);

    if (!trimmedFirst) {
      setFieldError({ field: "first_name", message: "Enter your first name." });
      return;
    }
    if (!trimmedLast) {
      setFieldError({ field: "last_name", message: "Enter your last name." });
      return;
    }
    if (!dob) {
      setFieldError({
        field: "date_of_birth",
        message: "Enter your date of birth.",
      });
      return;
    }
    if (nat.length !== 2) {
      setFieldError({
        field: "nationality",
        message: "Use a 2-letter country code, e.g. DK.",
      });
      return;
    }
    if (res.length !== 2) {
      setFieldError({
        field: "residence_country",
        message: "Use a 2-letter country code, e.g. DK.",
      });
      return;
    }

    try {
      await save.mutateAsync({
        first_name: trimmedFirst,
        last_name: trimmedLast,
        date_of_birth: dob,
        nationality: nat,
        residence_country: res,
      });
      setEditing(false);
      toast.success("Wallet details saved.");
    } catch (err) {
      const detail = err as WalletDetailsError;
      // The server is the validator — render its per-field message under
      // the input it names, or a banner when it names none.
      if (detail.field) {
        setFieldError({ field: detail.field, message: detail.message });
      } else {
        setFormError(detail.message ?? "Could not save your details.");
      }
    }
  };

  return (
    <form className="space-y-3" onSubmit={handleSubmit} noValidate>
      <p className="text-sm">
        {/* Story 8.3 — was "before you can add or withdraw funds". There is
            no withdrawal, and this string was NOT in Story 8.1's inventory:
            it was found by the new WalletPage absence-proof, which is the
            argument for writing that test rather than deleting the payout
            tests outright. */}
        We need these before you can top up your balance. They go to our
        payment partner for identity verification.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="wallet-details-first-name">First name</Label>
          <Input
            id="wallet-details-first-name"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setFieldError(null);
            }}
            disabled={save.isPending}
          />
          {errFor("first_name") && (
            <p className="text-sm text-destructive" role="alert">
              {errFor("first_name")}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <Label htmlFor="wallet-details-last-name">Last name</Label>
          <Input
            id="wallet-details-last-name"
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setFieldError(null);
            }}
            disabled={save.isPending}
          />
          {errFor("last_name") && (
            <p className="text-sm text-destructive" role="alert">
              {errFor("last_name")}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="wallet-details-dob">Date of birth</Label>
        <Input
          id="wallet-details-dob"
          type="date"
          autoComplete="bday"
          value={dob}
          onChange={(e) => {
            setDob(e.target.value);
            setFieldError(null);
          }}
          disabled={save.isPending}
        />
        {errFor("date_of_birth") && (
          <p className="text-sm text-destructive" role="alert">
            {errFor("date_of_birth")}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="wallet-details-nationality">Nationality</Label>
          <Input
            id="wallet-details-nationality"
            type="text"
            autoComplete="off"
            maxLength={2}
            placeholder="DK"
            value={nationality}
            onChange={(e) => {
              setNationality(normalizeCountry(e.target.value));
              setFieldError(null);
            }}
            disabled={save.isPending}
          />
          {errFor("nationality") && (
            <p className="text-sm text-destructive" role="alert">
              {errFor("nationality")}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <Label htmlFor="wallet-details-residence">
            Country of residence
          </Label>
          <Input
            id="wallet-details-residence"
            type="text"
            autoComplete="off"
            maxLength={2}
            placeholder="DK"
            value={residence}
            onChange={(e) => {
              setResidence(normalizeCountry(e.target.value));
              setFieldError(null);
            }}
            disabled={save.isPending}
          />
          {errFor("residence_country") && (
            <p className="text-sm text-destructive" role="alert">
              {errFor("residence_country")}
            </p>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Nationality and residence use 2-letter ISO country codes (e.g. DK, DE,
        FR).
      </p>

      {formError && (
        <p className="text-sm text-destructive" role="alert">
          {formError}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button type="submit" variant="primary" disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save details"}
        </Button>
        {complete && (
          <Button
            type="button"
            variant="tertiary"
            onClick={() => {
              setInitialized(false);
              setEditing(false);
            }}
            disabled={save.isPending}
          >
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
